import React, { useMemo, useState } from "react";
import { MiBox, MiList, MiModalConfirm } from "@miplatform/components";
import {
  BUTTON_LABELS,
  CONFIG_PAGES,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useDebounce, useRouter } from "@miplatform/hooks";
import { IInspectionAuthorityResponse } from "@miplatform/types";
import { mdiPencilOutline, mdiTrashCanOutline } from "@mdi/js";
import { AddButton } from "@mibuildweb/components";
import { useInspectionAuthorities, usePermissions } from "@mibuildweb/hooks";
import { useTheme } from "@mui/material";
import { CONFIG_PATHS } from "../../../routes/config/constants";
import { ConfigPageLayout } from "../ConfigPageLayout";
import { ConfigCard } from "../components";


export function ConfigInspectionAuthoritiesList() {
  const { navigate } = useRouter();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [triggerRefresh, setTriggerRefresh] = useState(false);
  const [selectedInspectionAuthority, setSelectedInspectionAuthority] =
    useState<IInspectionAuthorityResponse | null>(null);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const {
    deleteInspectionAuthority,
    getInspectionAuthorities,
    inspectionAuthorities,
    setInspectionAuthorities,
    recentInspectionAuthorities,
    setRecentInspectionAuthorities,
    getRecentInspectionAuthorities,
    isLoading,
  } = useInspectionAuthorities();

  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(PERMISSIONS.OPERATIONS.ADMINISTRATION);
  const canEdit = hasPermission(PERMISSIONS.OPERATIONS.ADMINISTRATION);
  const canDelete = hasPermission(PERMISSIONS.OPERATIONS.ADMINISTRATION);

  const BASE_PATH = CONFIG_PATHS[CONFIG_PAGES.INSPECTION_AUTHORITIES];

  const refreshInspectionAuthorities = useMemo(
    () => ({ triggerRefresh }),
    [triggerRefresh]
  );

  const refreshData = () => setTriggerRefresh((prev) => !prev);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleAdd = () => {
    navigate(`${BASE_PATH}/add-inspection-authority`);
  };

  const handleEdit = (item: IInspectionAuthorityResponse) => {
    navigate(`${BASE_PATH}/${item.id}`);
  };

  const handleOpenDeleteModal = (item: IInspectionAuthorityResponse) => {
    setSelectedInspectionAuthority(item);
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setSelectedInspectionAuthority(null);
  };

  const handleDelete = async () => {
    if (selectedInspectionAuthority) {
      await deleteInspectionAuthority({
        id: selectedInspectionAuthority.id,
        modifiedOn: selectedInspectionAuthority.modifiedOn,
        name: selectedInspectionAuthority.name,
      });
    }
    handleCloseDeleteModal();
    refreshData();
  };

  return (
    <ConfigPageLayout
      title="Inspection Authorities"
      search={search}
      onHandleSearch={handleSearchChange}
      hideOnBack
      loading={isLoading}
      titleActionComponent={
        canCreate && (
          <AddButton onClick={handleAdd} label="Add Inspection Authority" />
        )
      }
    >
      <MiBox>
        <MiList
          viewType="card"
          dataProvider={getInspectionAuthorities}
          loading={isLoading}
          data={inspectionAuthorities ?? []}
          setData={setInspectionAuthorities}
          CardComponent={InspectionAuthorityCard}
          cardProps={{
            canEdit,
            canDelete,
            onEdit: handleEdit,
            onDelete: handleOpenDeleteModal,
            onClick: handleEdit,
          }}
          paginationMode={PAGINATION.SERVER}
          search={debouncedSearch}
          recentData={recentInspectionAuthorities}
          setRecentData={setRecentInspectionAuthorities}
          recentDataProvider={getRecentInspectionAuthorities}
          refresh={refreshInspectionAuthorities}
          emptyListProps={{
            text: "No Inspection Authorities found",
          }}
        />
      </MiBox>
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={openDeleteModal}
        description={`You are about to delete ${selectedInspectionAuthority?.name}. Are you sure you would like to continue?`}
        textPositive={BUTTON_LABELS.DELETE}
        handlePositive={handleDelete}
        handleNegative={handleCloseDeleteModal}
        buttonPositiveColor="error"
      />
    </ConfigPageLayout>
  );
}

function InspectionAuthorityCard({
  item,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onClick,
}: Readonly<{
  item: IInspectionAuthorityResponse;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (item: IInspectionAuthorityResponse) => void;
  onDelete: (item: IInspectionAuthorityResponse) => void;
  onClick?: (item: IInspectionAuthorityResponse) => void;
}>) {
  const theme = useTheme();

  const menuActions = [];
  if (canEdit) {
    menuActions.push({
      label: BUTTON_LABELS.EDIT,
      action: () => onEdit(item),
      icon: mdiPencilOutline,
    });
  }
  if (canDelete) {
    menuActions.push({
      label: BUTTON_LABELS.DELETE,
      action: () => onDelete(item),
      icon: mdiTrashCanOutline,
      color: theme.palette.red.main,
    });
  }

  return (
    <ConfigCard
      title={item.name}
      modifiedOn={item.modifiedOn}
      createdOn={item.createdOn}
      menuActions={menuActions}
      onClick={() => onClick?.(item)}
    />
  );
}
